<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { UiRadioGroupVariant, type UiRadioGroupOption } from '@afs/design-system'

import { defaultAmount } from '~/basics/const'
import { durationProps, useSimulatorData } from '~/composables/useSimulatorData'
import type { StepOffersProps } from '~/components/simulator/StepOffers/interface'
import type { SimulatorProjectCardType } from '~/extensions/simulator/simulator.type'
import { useData } from 'vike-vue/useData'
import type { SimulatorPageData } from '@/pages/(subscription)/+data'
import { usePageContext } from 'vike-vue/usePageContext'

defineProps<StepOffersProps>()
const { data, selectedOfferLabel } = useSimulatorData()
const { urlParsed } = usePageContext()

const container = ref<HTMLElement | null>(null)

defineExpose({
  container,
})

const { simulatorSubProjects } = useData<SimulatorPageData>()

const projectMap = new Map(
  simulatorSubProjects?.map((simulatorSubProject) => [simulatorSubProject.projectId, simulatorSubProject.project]),
)
const uniqueProject = [...projectMap.values()].filter((item): item is SimulatorProjectCardType => item !== undefined)

const productOptions = uniqueProject.map((project) => {
  return {
    id: project.id,
    value: project.id,
    label: project.label,
    icon: project.icon,
    name: 'offer',
  }
})

const subProductsOptions = computed(() => {
  return (
    simulatorSubProjects
      ?.filter((subProject) => subProject.projectId === data.value.offers.offer)
      .map((subProject) => {
        return {
          id: subProject.id,
          value: subProject.projectType,
          label: subProject.label,
          icon: subProject.icon,
          name: 'sub-offer',
        }
      }) || []
  )
})

const onOfferSelect = (e: UiRadioGroupOption) => {
  data.value.offers.subOffer = ''
  data.value.amount = data.value.amount === Number(urlParsed.search.amount) ? data.value.amount : defaultAmount
  data.value.duration = [durationProps.value.min]
  selectedOfferLabel.value = e.label
}

const onSubOfferSelect = () => {
  data.value.duration = [durationProps.value.min]
  data.value.amount = data.value.amount === Number(urlParsed.search.amount) ? data.value.amount : defaultAmount
}

onMounted(() => {
  selectedOfferLabel.value = productOptions.find((option) => option.id === data.value.offers.offer)?.label || ''
})
</script>

<template>
  <section
    ref="container"
    class="SimulatorStepOffers radius-m border-normal border-blue-01"
    tabidex="-1"
  >
    <molecules-ui-fieldset :legend="$t('simulator.offersStep.offer.title')">
      <molecules-ui-radio-group
        v-model="data.offers.offer"
        class="SimulatorStepOffers__input"
        :options="productOptions"
        :variant="UiRadioGroupVariant.BLOC"
        nb-columns-small-screen="2"
        nb-columns-medium-screen="2"
        @radio-group::on-change="onOfferSelect"
      />
      <p
        v-if="errors?.['offers-offer']"
        class="mt-2 label color-red-01"
      >
        {{ errors['offers-offer'] }}
      </p>
    </molecules-ui-fieldset>
    <molecules-ui-fieldset
      v-if="data.offers.offer"
      :legend="$t('simulator.offersStep.subOffer.title', { product: selectedOfferLabel })"
    >
      <molecules-ui-radio-group
        v-model="data.offers.subOffer"
        class="SimulatorStepOffers__input"
        :options="subProductsOptions"
        :variant="UiRadioGroupVariant.BLOC"
        nb-columns-small-screen="2"
        nb-columns-medium-screen="2"
        @radio-group::on-change="onSubOfferSelect"
      />
      <p
        v-if="errors?.['offers-subOffer']"
        class="mt-2 label color-red-01"
      >
        {{ errors['offers-subOffer'] }}
      </p>
    </molecules-ui-fieldset>
  </section>
</template>

<style lang="scss" scoped>
@use 'utilities/mq';

.SimulatorStepOffers {
  overflow: hidden;
}
</style>
